import React from "react";
import { Field, useField } from "react-final-form";
import { Box, FormHelperText, FormLabel, InputAdornment, Table, TableBody, TableContainer, TableHead, Typography } from "@mui/material";

import { CheckboxField } from "fond/form/fields";
import { NumericField } from "fond/form/fields/NumericField";
import { TRANSFORMER_SIZE_OPTIONS, TransformerSizeDescriptor } from "fond/project/panels/MuPower/architecture/constants";
import { atLeastOneEnabled, calculateMaxSecondaryLoadAmps, validateWhenEnabled } from "fond/project/panels/MuPower/architecture/helpers";
import { formatNumber } from "fond/utils/number";
import { integer, requireNonZero } from "fond/utils/validation";

import { TableCell, TableHeadCell, TableRow } from "./MuPowerArchitectureModal.styles";

interface TransformerSizeTableRowProps {
  option: TransformerSizeDescriptor;
  index: number;
}

const TransformerSizeTableRow: React.FC<TransformerSizeTableRowProps> = ({ option, index }: TransformerSizeTableRowProps) => {
  const {
    input: { checked: enabled },
  } = useField(`Transformers.Options[${index}].Enabled`, { subscription: { value: true }, type: "checkbox" });
  const {
    input: { value: averageMeterLoadAmps },
  } = useField("Transformers.AverageMeterLoadAmps", { subscription: { value: true } });
  const {
    input: { value: averageAddressCount },
  } = useField(`Transformers.Options[${index}].AverageAddressCount`, { subscription: { value: true } });

  const maxSecondaryLoadAmps = calculateMaxSecondaryLoadAmps(averageMeterLoadAmps, averageAddressCount);
  const maxSecondaryLoadDisplay = maxSecondaryLoadAmps !== undefined ? `${formatNumber(maxSecondaryLoadAmps)} A` : "—";

  return (
    <TableRow data-testid={`transformer-size-row-${option.Size}`}>
      <TableCell sx={{ pl: 1.5 }}>
        <CheckboxField name={`Transformers.Options[${index}].Enabled`} label={`${option.RatingKva} kVA`} size="small" />
      </TableCell>
      <TableCell>
        <NumericField
          name={`Transformers.Options[${index}].AverageAddressCount`}
          disabled={!enabled}
          margin="none"
          extraValidators={[validateWhenEnabled((values) => values.Transformers.Options[index].Enabled, integer)]}
        />
      </TableCell>
      <TableCell sx={{ px: 1.5 }}>
        <Typography variant="body2" color={enabled ? "text.primary" : "text.disabled"}>
          {enabled ? maxSecondaryLoadDisplay : "not available to design"}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

const TransformersTab: React.FC = () => (
  <>
    <Box>
      <Box maxWidth={320}>
        <NumericField
          name="Transformers.AverageMeterLoadAmps"
          label="Average meter load"
          required
          min={0}
          extraValidators={[integer, requireNonZero()]}
          InputProps={{ endAdornment: <InputAdornment position="end">A per meter</InputAdornment> }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" component="div">
        Load assumed for every address. Transformer capacities below are derived from this value.
      </Typography>
    </Box>
    <Box mt={2}>
      <FormLabel required>Transformer sizes</FormLabel>
      <Typography variant="caption" color="text.secondary" component="div">
        Capacity is average meter load multiplied by the average number of addresses fed.
      </Typography>
      <TableContainer sx={{ borderRadius: 1, border: (theme) => `1px solid ${theme.palette.divider}`, my: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow className="table-head-row">
              <TableHeadCell width={160}>
                <Typography>Size</Typography>
              </TableHeadCell>
              <TableHeadCell width={220}>
                <Typography>Average addresses fed</Typography>
              </TableHeadCell>
              <TableHeadCell>
                <Typography>Maximum secondary load</Typography>
              </TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {TRANSFORMER_SIZE_OPTIONS.map((option, index) => (
              <TransformerSizeTableRow key={option.Size} option={option} index={index} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Field
        name="Transformers.OptionsGroup"
        validate={atLeastOneEnabled((values) => values.Transformers.Options, "Enable at least one transformer size")}
        render={({ meta }) => (meta.error && meta.submitFailed ? <FormHelperText error>{meta.error}</FormHelperText> : null)}
      />
    </Box>
  </>
);

export default TransformersTab;
